import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useLocation } from 'react-router-dom'
import { useOrgStore } from '@/stores/orgStore'
import { alertsApi } from '@/api/clients/alertsApi'
import type { AlertConfig, Band, AlertEvent } from '@/api/types'
import { PageHeader, Card, Button, Field, TextInput, SelectInput } from '@/components/common/ui'
import { BandBadge } from '@/components/common/Badge'
import { QueryState } from '@/components/common/State'
import OverviewTab from '@/pages/Overview'

const KIND_ICON: Record<string, string> = {
  new_critical: 'priority_high',
  threshold_breach: 'trending_up',
  hndl_open: 'lock_clock',
  system_risk: 'account_tree',
  source_risk: 'dataset',
}
const SEVERITIES = ['critical', 'high', 'medium', 'info'] as const
const SEV_HEX: Record<string, string> = { critical: '#C22221', high: '#e8734f', medium: '#eab308', info: '#94a3b8' }

function timeAgo(iso: string): string {
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return ''
  const sec = Math.max(0, Math.floor((Date.now() - t) / 1000))
  if (sec < 60) return 'just now'
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min}m ago`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}h ago`
  const d = Math.floor(hr / 24)
  if (d < 30) return `${d}d ago`
  const mo = Math.floor(d / 30)
  return mo < 12 ? `${mo}mo ago` : `${Math.floor(mo / 12)}y ago`
}

function AlertRow({ e, orgId }: { e: AlertEvent; orgId: string }) {
  const qc = useQueryClient()
  const location = useLocation()
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['alert-events', orgId] })
    qc.invalidateQueries({ queryKey: ['alert-unread', orgId] })
  }
  const ack = useMutation({ mutationFn: () => alertsApi.ackEvent(orgId, e.id), onSuccess: invalidate })
  const unack = useMutation({ mutationFn: () => alertsApi.unackEvent(orgId, e.id), onSuccess: invalidate })
  const resolve = useMutation({ mutationFn: () => alertsApi.resolveEvent(orgId, e.id), onSuccess: invalidate })
  const reopen = useMutation({ mutationFn: () => alertsApi.reopenEvent(orgId, e.id), onSuccess: invalidate })

  const title =
    e.scope_type === 'system' || e.scope_type === 'source'
      ? e.scope_name || e.inventory_name || (e.scope_type === 'source' ? 'Source' : 'System')
      : e.asset_name || e.message || 'Asset alert'
  const to =
    e.scope_type === 'system' && e.scope_id && e.scope_name
      ? `/inventories/${e.scope_id}?tab=risk`
      : e.scope_type === 'asset' && e.asset_id
        ? `/assets/${e.asset_id}`
        : null

  return (
    <li className="flex items-center gap-3 px-5 py-3">
      <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: SEV_HEX[e.severity] ?? '#94a3b8' }} title={e.severity} />
      <span className="material-icons text-[18px] text-subtle-light dark:text-subtle-dark shrink-0">{KIND_ICON[e.kind] ?? 'notifications'}</span>
      <BandBadge band={e.band as Band} />
      <div className="min-w-0 flex-1">
        {to ? (
          <Link to={to} state={e.scope_type === 'system' ? undefined : { backgroundLocation: location }} className="block truncate text-primary dark:text-secondary hover:underline">
            {title}
          </Link>
        ) : (
          <span className="block truncate text-text-light dark:text-text-dark">{title}</span>
        )}
        <span className="block truncate text-xs text-subtle-light dark:text-subtle-dark">
          <span className="capitalize">{e.scope_type}</span> · {e.message}
        </span>
      </div>
      <span className="shrink-0 text-xs text-subtle-light dark:text-subtle-dark" title={new Date(e.created_at).toLocaleString()}>
        {timeAgo(e.created_at)}
      </span>
      <div className="shrink-0 flex items-center gap-1">
        {e.status === 'open' && (
          <button onClick={() => ack.mutate()} disabled={ack.isPending} className="text-xs px-2 py-1 rounded-md text-primary dark:text-secondary hover:bg-primary/5" title="Acknowledge">
            Ack
          </button>
        )}
        {e.status === 'acknowledged' && (
          <button onClick={() => unack.mutate()} disabled={unack.isPending} className="text-xs px-2 py-1 rounded-md text-subtle-light dark:text-subtle-dark hover:bg-primary/5" title="Undo acknowledge">
            Acked · Undo
          </button>
        )}
        {e.status === 'resolved' ? (
          <button onClick={() => reopen.mutate()} disabled={reopen.isPending} className="text-xs px-2 py-1 rounded-md text-primary dark:text-secondary hover:bg-primary/5" title="Reopen (undo resolve)">
            Reopen
          </button>
        ) : (
          <button onClick={() => resolve.mutate()} disabled={resolve.isPending} className="text-xs px-2 py-1 rounded-md text-subtle-light dark:text-subtle-dark hover:bg-primary/5" title="Resolve">
            Resolve
          </button>
        )}
      </div>
    </li>
  )
}

export default function Alerts() {
  const { currentOrgId: orgId } = useOrgStore()
  const qc = useQueryClient()

  const [tab, setTab] = useState<'overview' | 'feed' | 'config'>('overview')
  const [severity, setSeverity] = useState('')
  const [scope, setScope] = useState('')
  const [status, setStatus] = useState('')

  const events = useQuery({
    queryKey: ['alert-events', orgId, { severity, scope, status }],
    queryFn: () => alertsApi.events(orgId, { severity: severity || undefined, scope_type: scope || undefined, status: status || undefined, limit: 200 }),
    enabled: !!orgId,
    retry: false,
  })

  const sync = useMutation({
    mutationFn: () => alertsApi.syncEvents(orgId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['alert-events', orgId] })
      qc.invalidateQueries({ queryKey: ['alert-unread', orgId] })
    },
  })

  const config = useQuery({ queryKey: ['alert-config', orgId], queryFn: () => alertsApi.getConfig(orgId), enabled: !!orgId })
  const [cfg, setCfg] = useState<AlertConfig | null>(null)
  useEffect(() => {
    if (config.data) setCfg(config.data)
  }, [config.data])
  const save = useMutation({
    mutationFn: () => alertsApi.setConfig(orgId, cfg!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['alert-config', orgId] }),
  })

  return (
    <div>
      <PageHeader title="Alerts" subtitle="Risk overview and notifications by severity and scope" />

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-border-light dark:border-border-dark mb-5">
        {([
          { key: 'overview', label: 'Overview', icon: 'insights' },
          { key: 'feed', label: 'Notifications', icon: 'notifications' },
          { key: 'config', label: 'Configuration', icon: 'tune' },
        ] as const).map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t.key
                ? 'border-primary text-primary dark:border-secondary dark:text-secondary'
                : 'border-transparent text-subtle-light dark:text-subtle-dark hover:text-text-light dark:hover:text-text-dark'
            }`}
          >
            <span className="material-icons text-base">{t.icon}</span>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && <OverviewTab orgId={orgId} />}

      {tab === 'feed' && (
        <>
          {/* Filter bar */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="w-40">
              <SelectInput value={severity} onChange={(e) => setSeverity(e.target.value)}>
                <option value="">All severities</option>
                {SEVERITIES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </SelectInput>
            </div>
            <div className="w-36">
              <SelectInput value={scope} onChange={(e) => setScope(e.target.value)}>
                <option value="">All scopes</option>
                <option value="asset">Asset</option>
                <option value="source">Source</option>
                <option value="system">System</option>
              </SelectInput>
            </div>
            <div className="w-40">
              <SelectInput value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">Open + acked</option>
                <option value="open">Open</option>
                <option value="acknowledged">Acknowledged</option>
                <option value="resolved">Resolved</option>
              </SelectInput>
            </div>
            <div className="ml-auto flex items-center gap-2">
              {sync.data && (
                <span className="text-xs text-subtle-light dark:text-subtle-dark">
                  +{sync.data.created} new · {sync.data.resolved} resolved
                </span>
              )}
              <Button variant="ghost" icon="sync" disabled={sync.isPending} onClick={() => sync.mutate()}>
                {sync.isPending ? 'Syncing…' : 'Sync now'}
              </Button>
            </div>
          </div>

          <Card className="!p-0">
            <QueryState
              isLoading={events.isLoading}
              error={events.error}
              isEmpty={events.data?.length === 0}
              emptyProps={{ icon: 'notifications_off', title: 'No alerts', hint: 'Run "Sync now" to materialise current alert conditions.' }}
            >
              <ul className="divide-y divide-border-light dark:divide-border-dark">
                {(events.data ?? []).map((e) => (
                  <AlertRow key={e.id} e={e} orgId={orgId} />
                ))}
              </ul>
            </QueryState>
          </Card>
        </>
      )}

      {tab === 'config' && (
        <Card title="Alert configuration">
          {cfg && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <label className="flex items-center gap-2 text-sm text-text-light dark:text-text-dark">
                <input type="checkbox" checked={cfg.new_critical} onChange={(e) => setCfg({ ...cfg, new_critical: e.target.checked })} className="rounded" />
                Alert on new criticals
              </label>
              <Field label="Band threshold">
                <SelectInput value={cfg.band_threshold} onChange={(e) => setCfg({ ...cfg, band_threshold: e.target.value })}>
                  {['critical', 'high', 'medium', 'low'].map((b) => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </SelectInput>
              </Field>
              <Field label="HNDL window (days)">
                <TextInput type="number" value={cfg.hndl_window_days} onChange={(e) => setCfg({ ...cfg, hndl_window_days: Number(e.target.value) })} />
              </Field>
              <Field label="Route to">
                <TextInput value={cfg.route_to} onChange={(e) => setCfg({ ...cfg, route_to: e.target.value })} placeholder="security-team@company.com" />
              </Field>
              <div className="sm:col-span-2 lg:col-span-4">
                {save.error && <p className="mb-2 text-sm text-error">{(save.error as Error).message}</p>}
                <Button variant="primary" icon="save" disabled={save.isPending} onClick={() => save.mutate()}>
                  {save.isPending ? 'Saving…' : 'Save config'}
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  )
}
